import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createApiClient } from '../lib/api-client';
import type { paths } from '../lib/api-types';
import { useApiStore } from '../store/api';
import { useAuthStore } from '../store/auth';

type Participant =
  paths['/v1/conversations/{id}/participants']['get']['responses']['200']['content']['application/json']['participants'][0];

export interface UseParticipantsReturn {
  participants: Participant[];
  loading: boolean;
  error: Error | null;
  addParticipant: (
    userId: string,
    options?: { role?: 'member' | 'guest'; display_name?: string }
  ) => Promise<Participant>;
  removeParticipant: (userId: string) => Promise<void>;
  refreshParticipants: () => Promise<void>;
}

/**
 * Hook for managing conversation participants.
 * Fetches the participant list and provides mutation methods for add/remove.
 *
 * @param conversationId - The conversation to manage participants for
 */
export function useParticipants(conversationId: string): UseParticipantsReturn {
  const apiUrl = useApiStore((state) => state.apiUrl);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const queryClient = useQueryClient();

  const queryKey = ['participants', conversationId];

  const {
    data: participants = [],
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const client = createApiClient(apiUrl);
      const { data, error: apiError } = await client.GET('/v1/conversations/{id}/participants', {
        params: { path: { id: conversationId } },
      });

      if (apiError || !data) {
        throw new Error('Failed to load participants');
      }

      return data.participants;
    },
    enabled: isAuthenticated && !!conversationId,
    staleTime: 10000, // Consider data fresh for 10 seconds
  });

  const addParticipantMutation = useMutation({
    mutationFn: async ({
      userId,
      role,
      display_name,
    }: {
      userId: string;
      role?: 'member' | 'guest';
      display_name?: string;
    }) => {
      const client = createApiClient(apiUrl);
      const { data, error: apiError } = await client.POST('/v1/conversations/{id}/participants', {
        params: { path: { id: conversationId } },
        body: {
          user_id: userId,
          role: role || 'member',
          display_name,
        },
      });

      if (apiError || !data) {
        throw new Error('Failed to add participant');
      }

      return data;
    },
    onSuccess: (newParticipant) => {
      queryClient.setQueryData<Participant[]>(queryKey, (old = []) => [
        ...old,
        newParticipant as Participant,
      ]);
    },
  });

  const removeParticipantMutation = useMutation({
    mutationFn: async (userId: string) => {
      const client = createApiClient(apiUrl);
      const { error: apiError } = await client.DELETE(
        '/v1/conversations/{id}/participants/{userId}',
        {
          params: { path: { id: conversationId, userId } },
        }
      );

      if (apiError) {
        throw new Error('Failed to remove participant');
      }
    },
    onSuccess: (_data, userId) => {
      queryClient.setQueryData<Participant[]>(queryKey, (old = []) =>
        old.filter((p) => p.user_id !== userId)
      );
    },
  });

  return {
    participants,
    loading,
    error: error as Error | null,
    addParticipant: async (userId, options) => {
      const result = await addParticipantMutation.mutateAsync({
        userId,
        role: options?.role,
        display_name: options?.display_name,
      });
      return result as Participant;
    },
    removeParticipant: async (userId) => {
      await removeParticipantMutation.mutateAsync(userId);
    },
    refreshParticipants: async () => {
      await refetch();
    },
  };
}
